/* eslint-disable @typescript-eslint/no-explicit-any -- Angular uses "any" defaults in a lot of its form typing, so by extension, so do we. */
import {Injectable} from '@angular/core';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {AbstractControlOptions} from '@angular/forms';
import {ExtendedFormArray} from '@aviary/forms/extensions/extended-form-array/extended-form-array';
import {AngularAsyncValidator, AngularValidator, ExtendedAbstractControl} from '@aviary/forms/extensions/form-extensions.types';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {isExtendedFormArray, isExtendedFormControl, isExtendedFormGroup} from '@aviary/forms/form.helpers';
import {isArray, isObject} from '@aviary/store/common/typing.helpers';

@Injectable({providedIn: 'root'})
export class ExtendedNonNullableFormBuilder {

  public extendedControl<T, DataType extends Record<string, string> = Record<string, never>>(
    formState: any, validatorOrOpts?: AngularValidator<AbstractControlOptions>, asyncValidator?: AngularAsyncValidator
  ): ExtendedFormControl<T, DataType> {
    return new ExtendedFormControl<T, DataType>(formState, validatorOrOpts, asyncValidator);
  }

  public extendedGroup<T extends { [K in keyof T]: ExtendedAbstractControl } = any>(
    formState: { [key: string]: any },
    validatorOrOpts?: AngularValidator<AbstractControlOptions>,
    asyncValidator?: AngularAsyncValidator
  ): ExtendedFormGroup<T> {
    const controls: Record<string, ExtendedAbstractControl> = {};

    for (const key in formState) {
      controls[key] = this.createExtendedControl(formState[key]);
    }

    return new ExtendedFormGroup<T>(controls as any, validatorOrOpts, asyncValidator);
  }

  public extendedArray<T extends ExtendedAbstractControl = ExtendedAbstractControl>(
    controls: Array<T>, validatorOrOpts?: AngularValidator<AbstractControlOptions>, asyncValidator?: AngularAsyncValidator
  ): ExtendedFormArray<T> {
    return new ExtendedFormArray<T>(controls, validatorOrOpts, asyncValidator);
  }

  private createExtendedControl(formStateValue: any): ExtendedAbstractControl {
    if (isExtendedFormControl(formStateValue) || isExtendedFormGroup(formStateValue) || isExtendedFormArray(formStateValue)) return formStateValue;

    if (isArray(formStateValue)) {
      const [initialValue, validatorOrOpts, asyncValidator] =
        formStateValue as [any, AngularValidator<AbstractControlOptions> | undefined, AngularAsyncValidator | undefined];

      return new ExtendedFormControl(initialValue, validatorOrOpts, asyncValidator);
    }

    if (isObject(formStateValue) && ('validators' in formStateValue || 'asyncValidators' in formStateValue || 'updateOn' in formStateValue)) {
      return new ExtendedFormControl('', formStateValue);
    }

    return new ExtendedFormControl(formStateValue);
  }
}
